// Single source of truth mapping onboarding URL paths <-> wizard step + sub-step.
//
// URLs are nested: /onboarding/<step>/<sub-step> (e.g. /onboarding/academic-structure/levels).
// The <step> matches the left sidebar step; the <sub-step> matches the top sub-step inside it.
// This lets the backend deep-link a user (already auto-logged-in) straight to the next
// incomplete sub-section, and keeps the address bar in sync as the user navigates.
// The shared step-tree contract, committed identically to portal, node-api and
// node-api-subscription. See the "Analytics position names" block below.
// The main-step grouping contract: which positions the sidebar folds into one Next click.
// Committed identically to portal and node-api-subscription. See "Main-step grouping" below.
import mainSteps from './onboarding-main-steps.json'
import stepTree from './onboarding-step-tree.json'

export type ManualTab = 'teachers' | 'students' | 'lessonPlanning' | 'classes' | 'subjects'

export interface OnboardingStep {
  /** URL slug for the step (left sidebar). */
  slug: string
  /** Wizard `currentStep`. */
  step: number
  /** Top sub-steps inside the step, in display order. Empty when the step has no sub-steps. */
  subSteps: readonly string[]
}

export const ONBOARDING_STEPS: readonly OnboardingStep[] = [
  {slug: 'timetable-type', step: 1, subSteps: []},
  {
    slug: 'school-setup',
    step: 2,
    subSteps: ['branches', 'academic-session', 'frequency', 'venues', 'timings'],
  },
  {
    slug: 'academic-structure',
    step: 3,
    subSteps: ['curriculum', 'levels', 'classes', 'sections', 'review', 'subjects'],
  },
  {
    slug: 'assign-subjects',
    step: 4,
    subSteps: ['teachers', 'review-teachers', 'students', 'lesson-planning'],
  },
  {slug: 'summary', step: 5, subSteps: []},
] as const

/**
 * Old step slugs -> current ones. Persisted resume pointers (user preferences) and
 * backend deep-link URLs (nudge emails already sent) still carry the old slugs, so
 * every slug lookup normalizes through this map — renaming a step must never strand
 * a returning user back at step 1.
 *
 * The Data Import step was removed entirely (every user now goes through the manual
 * flow), so pointers that saved it resume at the step that follows it.
 */
const LEGACY_STEP_SLUGS: Readonly<Record<string, string>> = {
  mode: 'timetable-type',
  'core-setup': 'school-setup',
  curriculum: 'academic-structure',
  'data-input': 'assign-subjects',
  'data-import': 'assign-subjects',
  'teachers-allocation': 'assign-subjects',
}

/** Resolve a possibly-legacy step slug to its current canonical slug. */
export const canonicalStepSlug = (slug?: string | null): string | undefined =>
  slug ? (LEGACY_STEP_SLUGS[slug] ?? slug) : undefined

/**
 * Sub-step aliases -> canonical URL slugs. Two sources feed this:
 *  - legacy slugs in persisted resume pointers and in nudge-email deep links already sent;
 *  - Core Setup field ids, which Step2BasicSetup reports verbatim and which do not all match
 *    their URL slug (the section labelled "Academic Session" is the `duration` field).
 * Every sub-step lookup normalizes through this map, so an old URL still lands correctly.
 */
const SUBSTEP_SLUG_ALIASES: Readonly<Record<string, string>> = {
  duration: 'academic-session',
}

/** Resolve a legacy sub-step slug — or a Core Setup field id — to its canonical URL slug. */
export const canonicalSubStepSlug = (slug?: string | null): string | null =>
  slug ? (SUBSTEP_SLUG_ALIASES[slug] ?? slug) : null

export const getStepBySlug = (slug?: string): OnboardingStep | undefined => {
  const canonical = canonicalStepSlug(slug)
  return canonical ? ONBOARDING_STEPS.find((s) => s.slug === canonical) : undefined
}

/** The wizard `currentStep` a step slug maps to. */
export const stepNumberForSlug = (step: OnboardingStep): number => step.step

/** The step definition for a wizard `currentStep`. */
export const stepForNumber = (currentStep: number): OnboardingStep | undefined =>
  ONBOARDING_STEPS.find((s) => s.step === currentStep)

/** Build the canonical URL path for a step + (optional) sub-step. */
export const buildOnboardingPath = (stepSlug: string, subStepSlug?: string | null): string =>
  subStepSlug ? `/onboarding/${stepSlug}/${subStepSlug}` : `/onboarding/${stepSlug}`

// --- Linear progress ordering (resume "furthest reached") --------------------------

export interface OnboardingPosition {
  stepSlug: string
  subStepSlug: string | null
}

/**
 * Every reachable (step, sub-step) position flattened into display order. Steps with no
 * sub-steps contribute a single position with `subStepSlug: null`. Used to rank how far a
 * user has progressed so resume only ever moves forward (never drags progress backward).
 */
export const ONBOARDING_POSITION_ORDER: readonly OnboardingPosition[] = ONBOARDING_STEPS.flatMap(
  (step): OnboardingPosition[] =>
    step.subSteps.length
      ? step.subSteps.map((sub) => ({stepSlug: step.slug, subStepSlug: sub}))
      : [{stepSlug: step.slug, subStepSlug: null}]
)

/**
 * Ordinal rank of a position in the onboarding flow (higher = further along). Returns -1 for
 * an unknown step. When the sub-step is missing/unknown, falls back to the step's first
 * position so a bare step slug still ranks sensibly.
 */
export const onboardingPositionRank = (
  stepSlug?: string | null,
  subStepSlug?: string | null
): number => {
  const slug = canonicalStepSlug(stepSlug)
  if (!slug) return -1
  const sub = canonicalSubStepSlug(subStepSlug)
  const exact = ONBOARDING_POSITION_ORDER.findIndex(
    (p) => p.stepSlug === slug && p.subStepSlug === sub
  )
  if (exact >= 0) return exact
  return ONBOARDING_POSITION_ORDER.findIndex((p) => p.stepSlug === slug)
}

// --- Analytics position names (consumed by node-api's funnel writer) ----------------
//
// The wizard's position has TWO names: the (stepSlug, subStepSlug) pair the URL uses, and
// the flat slug the analytics funnel counts (`core_branches`, `curriculum_levels`, …).
// node-api used to derive the second from the first by holding its own copy of this whole
// tree and matching slugs exactly. That copy silently fell behind the 6 Aug reorder and the
// 7 Aug rename, and because a slug miss just returns -1, positions 1–13 stopped producing
// any funnel signal at all for five weeks while every test in every repo stayed green.
//
// So the wizard now STATES its position name in the preferences pointer and the server
// stops guessing. The slug path remains as node-api's fallback for pointers written by
// older builds — those never expire — but nothing new depends on the two trees agreeing.
//
// `onboarding-step-tree.json` is committed identically here, in node-api and in
// node-api-subscription; each repo asserts its own code against it. That file is the
// contract, this is its binding. (Imported at the top of this file.)

interface StepTreePosition {
  name: string
  stepSlug: string
  subStepSlug: string | null
  linear: boolean
  label: string
}

export const ONBOARDING_STEP_TREE_VERSION: string = stepTree.version

export const ONBOARDING_POSITION_NAMES: readonly StepTreePosition[] =
  stepTree.positions as StepTreePosition[]

/**
 * The analytics position name for a (step, sub-step) pair, or null when the pair is not a
 * real position. Slugs are canonicalized first, so a legacy pointer resolves too.
 */
export const onboardingPositionName = (
  stepSlug?: string | null,
  subStepSlug?: string | null
): string | null => {
  const slug = canonicalStepSlug(stepSlug)
  if (!slug) return null
  const sub = canonicalSubStepSlug(subStepSlug)
  const exact = ONBOARDING_POSITION_NAMES.find((p) => p.stepSlug === slug && p.subStepSlug === sub)
  if (exact) return exact.name
  // Bare step slug — same fallback onboardingPositionRank applies.
  return ONBOARDING_POSITION_NAMES.find((p) => p.stepSlug === slug)?.name ?? null
}

/**
 * The `manualData` fields each Core Setup position owns.
 *
 * Used to mark a screen's fields as user-confirmed once the user advances PAST it. The
 * wizard pre-fills several of these (the "Main Branch" row, the default subject frequency),
 * and `touchedSetupFieldsRef` deliberately excludes pre-filled values so a screen is never
 * recorded as saved merely for being viewed — each (tenant, sub-step) row is written once
 * and first write wins forever.
 *
 * That guard has a false negative, though, and it is the one QA keeps reporting: a user who
 * accepts the default branch name and clicks Next has genuinely completed the screen, but
 * touched nothing, so Branches never counted. Advancing is itself the confirmation — the
 * Next button is disabled until the screen is valid — which is the same rule node-api's
 * gating already applies server-side (`resolveSavedSignals`).
 *
 * Positions absent from this map own no preferences-backed fields: `mode` has its own
 * bucket, the curriculum positions write to `curriculum`/`classesDraft`, and `data_input`
 * plus the Assign Subjects tabs go to the bulk-import API.
 */
export const SETUP_FIELDS_BY_POSITION: Readonly<Record<string, readonly string[]>> = {
  core_branches: ['branches'],
  core_duration: ['durationName', 'durationStartDate', 'durationEndDate'],
  core_frequency: ['defaultSubjectFrequency', 'defaultFrequencyPer'],
  core_venues: ['venues', 'venuesAutoCount', 'venuesSameForClass'],
  core_timings: ['timings', 'break_timings'],
}

/**
 * Every setup field owned by a position STRICTLY BEFORE `rank`, i.e. the screens the user
 * has advanced past. Returns [] for rank <= 0.
 */
export const setupFieldsPassedBefore = (rank: number): string[] => {
  if (rank <= 0) return []
  const fields: string[] = []
  ONBOARDING_POSITION_NAMES.slice(0, rank).forEach((position) => {
    ;(SETUP_FIELDS_BY_POSITION[position.name] ?? []).forEach((field) => fields.push(field))
  })
  return fields
}

/**
 * Throw unless ONBOARDING_STEPS and the shared step tree describe the same wizard, in the
 * same order. Called by the unit suite — a mismatch is a build-time defect, and refusing to
 * render onboarding over an analytics column would be the worse failure.
 */
export const assertOnboardingStepTreeContract = (): string => {
  const fromSteps = ONBOARDING_POSITION_ORDER.map((p) => `${p.stepSlug}/${p.subStepSlug ?? ''}`)
  const fromTree = ONBOARDING_POSITION_NAMES.map((p) => `${p.stepSlug}/${p.subStepSlug ?? ''}`)
  if (fromSteps.join('|') !== fromTree.join('|')) {
    throw new Error(
      `onboardingSections: ONBOARDING_STEPS drifted from onboarding-step-tree.json ` +
        `(contract version ${ONBOARDING_STEP_TREE_VERSION}).\n` +
        `  ONBOARDING_STEPS: ${fromSteps.join(', ')}\n` +
        `  step tree:        ${fromTree.join(', ')}`
    )
  }
  const treeAliases = JSON.stringify(stepTree.stepSlugAliases)
  if (treeAliases !== JSON.stringify(LEGACY_STEP_SLUGS)) {
    throw new Error(
      `onboardingSections: LEGACY_STEP_SLUGS drifted from onboarding-step-tree.json ` +
        `(contract version ${ONBOARDING_STEP_TREE_VERSION}).`
    )
  }
  if (JSON.stringify(stepTree.subStepSlugAliases) !== JSON.stringify(SUBSTEP_SLUG_ALIASES)) {
    throw new Error(
      `onboardingSections: SUBSTEP_SLUG_ALIASES drifted from onboarding-step-tree.json ` +
        `(contract version ${ONBOARDING_STEP_TREE_VERSION}).`
    )
  }
  return ONBOARDING_STEP_TREE_VERSION
}

// --- Main-step grouping (consumed by the edit-tracking batches) ----------------------
//
// `onboarding-step-tree.json` states the wizard's POSITIONS in ordinal order, a write-side
// fact that must not move. `onboarding-main-steps.json` states a different thing: which
// positions the sidebar GROUPS into one main step, i.e. which complete together on one Next
// click. That grouping is exactly which `onboarding_substep_edited` rows ride in which
// click's batch, so the wizard reads it from the contract rather than holding a fifth
// hand-written copy of the step tree. node-api-subscription asserts its own code against the
// same file and refuses to start a run if they drift (ONBOARDING_EDIT_TRACKING spec 4.5).

interface MainStep {
  key: string
  label: string
  sidebar_step: number
  step_slug: string
  sub_steps: readonly string[]
}

export const ONBOARDING_MAIN_STEPS_VERSION: string = mainSteps.version

export const ONBOARDING_MAIN_STEPS: readonly MainStep[] = mainSteps.steps as MainStep[]

/** The event name the portal emits for an edited position, as the contract spells it. */
export const ONBOARDING_EDIT_EVENT_NAME: string = mainSteps.edit_event.event_name

/**
 * Every position that completes on the Next click of wizard `currentStep`, in funnel order.
 * Teacher Allocation (step 4) spans three contract rows, one per tab, so they concatenate.
 */
export const positionsCompletedByStep = (currentStep: number): string[] =>
  ONBOARDING_MAIN_STEPS.filter((s) => s.sidebar_step === currentStep).flatMap((s) => [
    ...s.sub_steps,
  ])

/** The wizard `currentStep` whose Next click completes `position`; undefined when none does. */
export const stepCompletingPosition = (position: string): number | undefined =>
  ONBOARDING_MAIN_STEPS.find((s) => s.sub_steps.includes(position))?.sidebar_step

/**
 * Throw unless onboarding-main-steps.json partitions the wizard's positions (minus the retired
 * ones and the milestone witnesses) and each main step maps onto a real wizard step. The
 * sibling of assertOnboardingStepTreeContract; called by the unit suite.
 */
export const assertOnboardingMainStepContract = (): string => {
  const version = ONBOARDING_MAIN_STEPS_VERSION
  const fail = (why: string): never => {
    throw new Error(
      `onboardingSections: onboarding-main-steps.json (contract version ${version}) ${why}`
    )
  }
  const positions = new Set(ONBOARDING_POSITION_NAMES.map((p) => p.name))
  const excluded = new Set<string>([
    ...mainSteps.retired_sub_steps,
    ...mainSteps.milestones.map((m: any) => m.witness_sub_step).filter(Boolean),
  ])
  const seen = new Set<string>()
  ONBOARDING_MAIN_STEPS.forEach((step) => {
    const wizardStep = getStepBySlug(step.step_slug)
    if (!wizardStep || wizardStep.step !== step.sidebar_step) {
      fail(`step "${step.key}" does not map onto wizard step ${step.sidebar_step}`)
    }
    step.sub_steps.forEach((name) => {
      if (!positions.has(name)) fail(`names unknown position "${name}"`)
      if (excluded.has(name)) fail(`groups retired position "${name}"`)
      if (seen.has(name)) fail(`groups position "${name}" twice`)
      seen.add(name)
    })
  })
  positions.forEach((name) => {
    if (!seen.has(name) && !excluded.has(name)) fail(`leaves position "${name}" ungrouped`)
  })
  if (ONBOARDING_EDIT_EVENT_NAME !== 'onboarding_substep_edited') {
    fail(
      `names edit event "${ONBOARDING_EDIT_EVENT_NAME}", the portal emits onboarding_substep_edited`
    )
  }
  return version
}

// --- Completion percentage + step labels (consumed by the backend nudge email) -------
//
// The wizard owns the step/sub-step tree, so it is the only place that can compute an
// accurate, sub-step-level completion percentage. It writes the number into the user's
// preferences (`OnboardingWizard.progress.progressPercent`) alongside the resume pointer;
// the backend renders it verbatim and does no step maths of its own.

/**
 * How far through onboarding a position is, as 0–100.
 *
 * Counts positions *completed*: sitting on a position means everything before it is done and
 * this one is still in progress. So the very first position is 0 and the last (Summary) stops
 * short of 100 — 100 would claim the wizard was finished, which only the completion call knows.
 * Returns null for an unknown step so callers can leave the field absent rather than guess.
 */
export const onboardingProgressPercent = (
  stepSlug?: string | null,
  subStepSlug?: string | null
): number | null => {
  const slug = canonicalStepSlug(stepSlug)
  if (!slug) return null
  const sub = canonicalSubStepSlug(subStepSlug)
  const positions = ONBOARDING_POSITION_ORDER
  const exact = positions.findIndex((p) => p.stepSlug === slug && p.subStepSlug === sub)
  const index = exact >= 0 ? exact : positions.findIndex((p) => p.stepSlug === slug)
  if (index < 0) return null
  return Math.round((index / positions.length) * 100)
}

/**
 * English UI labels, mirroring the sidebar (`ONBOARDING_WIZARD.SIDEBAR.STEP*`). Deliberately
 * NOT localized: this label is persisted for the backend email, which is English and renders
 * it verbatim — a label captured in the user's UI locale would leak into an English email.
 */
export const ONBOARDING_STEP_LABELS: Readonly<Record<string, string>> = {
  'timetable-type': 'Timetable Type',
  'school-setup': 'School Setup',
  'academic-structure': 'Academic Structure',
  'assign-subjects': 'Teacher Allocation',
  summary: 'Summary',
}

export const onboardingStepLabel = (stepSlug?: string | null): string | null => {
  const slug = canonicalStepSlug(stepSlug)
  return (slug && ONBOARDING_STEP_LABELS[slug]) || null
}

// --- Sub-step <-> child position mappings ------------------------------------------

const CORE_SETUP_SUBSTEPS = [
  'branches',
  'academic-session',
  'frequency',
  'venues',
  'timings',
] as const

/** Index into Core Setup's sub-step order for `Step2BasicSetup.initialSubStep`. */
export const coreSetupSubStepIndex = (subStepSlug?: string): number | undefined => {
  const slug = canonicalSubStepSlug(subStepSlug)
  if (!slug) return undefined
  const idx = CORE_SETUP_SUBSTEPS.indexOf(slug as (typeof CORE_SETUP_SUBSTEPS)[number])
  return idx >= 0 ? idx : undefined
}

export type CurriculumSection =
  | 'curriculum'
  | 'levels'
  | 'classes'
  | 'sections'
  | 'review'
  | 'subjects'

/** Curriculum sub-step slug -> `Step3Curriculum.initialSection` (the slugs match the section ids). */
export const curriculumSection = (subStepSlug?: string): CurriculumSection | undefined => {
  const valid: CurriculumSection[] = [
    'curriculum',
    'levels',
    'classes',
    'sections',
    'review',
    'subjects',
  ]
  return valid.includes(subStepSlug as CurriculumSection)
    ? (subStepSlug as CurriculumSection)
    : undefined
}

/** Assign Subjects sub-step slug <-> Step4Manual tab id. */
export const assignSubjectsTab = (
  subStepSlug?: string
): 'teachers' | 'students' | 'lessonPlanning' | undefined => {
  switch (subStepSlug) {
    case 'teachers':
    case 'review-teachers':
      return 'teachers'
    case 'students':
      return 'students'
    case 'lesson-planning':
      return 'lessonPlanning'
    default:
      return undefined
  }
}

/**
 * Which screen of the Teachers tab a sub-step slug opens: `teachers` is the Add Teachers entry
 * screen, `review-teachers` the full-page Review Teachers state inside the same tab.
 */
export const assignSubjectsTeachersPhase = (
  subStepSlug?: string
): 'entry' | 'review' | undefined => {
  switch (subStepSlug) {
    case 'teachers':
      return 'entry'
    case 'review-teachers':
      return 'review'
    default:
      return undefined
  }
}

export const assignSubjectsSlug = (tabId?: string): string | undefined => {
  switch (tabId) {
    case 'teachers':
      return 'teachers'
    case 'review-teachers':
      return 'review-teachers'
    case 'students':
      return 'students'
    case 'lessonPlanning':
      return 'lesson-planning'
    default:
      return undefined
  }
}
